import { computed, effectScope, ref, watch } from "vue";
import type { CountryCode } from "libphonenumber-js";
import api from "../api";
import { logError, logInfo } from "../helpers/logError";
import { useAuthSessionStore } from "../stores/authSession";

const currentOrganizationDefaultCountry = ref<CountryCode | undefined>(undefined);
let inflightOrgId: string | null = null;
// Detached effect scope so the watcher's lifetime is module-level — not bound
// to whichever component called useOrganization() first. Without this, when
// that component unmounts Vue auto-stops the watch and every later caller
// silently observes a frozen country code.
let scope: ReturnType<typeof effectScope> | null = null;

async function loadCountryForOrg(orgId: string): Promise<void> {
	if (inflightOrgId === orgId) {
		return;
	}
	inflightOrgId = orgId;
	// Drop the api `me` cache before fetching. useOrgRouteGuard also calls
	// invalidateMe on org change, but child components calling useOrganization()
	// mount BEFORE App.vue installs that guard — so the route guard's watcher
	// runs after this one. Without this defensive invalidate, getMe() can hand
	// back the previous org's cached data and freeze the country code at the
	// wrong value. invalidateMe is a single-field reset; double-invalidate is
	// the same as one.
	try {
		api.invalidateMe();
	} catch {
		// Cache reset is best-effort — fall through to getMe regardless.
	}
	try {
		const me = (await api.getMe()) as { organization?: { id?: string; defaultCountryCode?: string } | null };
		// Drop stale resolutions: another switch may have replaced inflightOrgId mid-flight.
		if (inflightOrgId !== orgId) {
			return;
		}
		const country = me.organization?.defaultCountryCode;
		currentOrganizationDefaultCountry.value = country ? (country as CountryCode) : undefined;
	} catch (err) {
		// Log so Datadog sees auth/network failures in the core /users/me path.
		// Downstream (RecipientPicker.defaultCountry) treats undefined as "require E.164".
		logError("useOrganization: failed to load /users/me", err);
		if (inflightOrgId === orgId) {
			currentOrganizationDefaultCountry.value = undefined;
			// Release the inflight slot so a follow-up trigger (e.g., the user
			// retries the org switch, or the same org id arrives via a token
			// rotation) actually re-fetches. Without this, a transient getMe
			// failure during the switch permanently freezes the country at
			// undefined until the user switches away and back.
			inflightOrgId = null;
		}
	}
}

function ensureScope(): void {
	if (scope) {
		return;
	}
	scope = effectScope(true);
	scope.run(() => {
		const authSession = useAuthSessionStore();
		watch(
			() => authSession.organizationId,
			(orgId) => {
				if (typeof orgId === "string" && orgId) {
					void loadCountryForOrg(orgId);
				} else {
					currentOrganizationDefaultCountry.value = undefined;
				}
			},
			{ immediate: true }
		);
	});
}

export function useOrganization() {
	const authSession = useAuthSessionStore();
	const currentOrganizationId = computed(() => authSession.organizationId ?? undefined);
	ensureScope();

	function isSameOrganization(organizationId: string | undefined): boolean {
		return organizationId === currentOrganizationId.value;
	}

	// LSP-16631: when Organization Details saves a new primary country for the
	// active org, the org id does not change, so the org-id watcher never re-runs
	// and the country ref stays stale until reload — leaving Share Report's
	// RecipientPicker parsing SMS numbers against the old country. Expose a
	// same-org updater so the caller can refresh the shared ref directly. Guarded
	// by the current org id: a mid-save org switch must not misattribute the saved
	// country to whatever org is now active.
	function updateCurrentOrganizationDefaultCountry(organizationId: string | undefined, countryCode: string | null | undefined): void {
		if (!organizationId || !isSameOrganization(organizationId)) {
			// Diagnostic breadcrumb (LSP-16631): the refresh is intentionally skipped when the
			// target is not the active org — e.g. a mid-save org switch. That silently re-strands
			// the stale country this fix exists to clear, so record which condition tripped; a
			// future "country didn't refresh after save" report is otherwise unreconstructable.
			// logInfo reaches Datadog (console.debug/info are dropped by forwardConsoleLogs).
			logInfo("useOrganization: skipped country-ref refresh (org not active)", {
				requestedOrgId: organizationId,
				currentOrgId: currentOrganizationId.value
			});
			return;
		}
		currentOrganizationDefaultCountry.value = countryCode ? (countryCode as CountryCode) : undefined;
		// A manual write is authoritative, fresher data for the active org (it carries the
		// just-saved value). Release any in-flight loadCountryForOrg claim for this same org —
		// started on the org-id change — so a slower /users/me response cannot resolve later and
		// silently clobber this value with the pre-save country. The load's success/error paths
		// both bail on `inflightOrgId !== orgId`, so nulling the slot makes them no-ops.
		if (inflightOrgId === organizationId) {
			inflightOrgId = null;
		}
	}

	return {
		currentOrganizationId,
		currentOrganizationDefaultCountry,
		isSameOrganization,
		updateCurrentOrganizationDefaultCountry
	};
}

// Test-only: tear down the module-level scope so each test starts clean.
export function _disposeUseOrganizationScope(): void {
	if (scope) {
		scope.stop();
		scope = null;
	}
	inflightOrgId = null;
	currentOrganizationDefaultCountry.value = undefined;
}
